/**
 * CookiePolicy — Sprint D (Feb 2026). 12 sections, verbatim from legal@.
 * Last Updated: July 18, 2026 (note the 18th, not 17th).
 */
import { Cookie } from 'lucide-react';
import {
  LegalPageShell, HeroCard, LegalLanguageBar, Section, P, Bullet, SubHeading,
  ContactBox, EmailLink, ExternalLink, LegalFooter,
} from '@/components/legal/LegalPrimitives';

export default function CookiePolicy() {
  return (
    <LegalPageShell title="Cookie Policy">
      <HeroCard icon={Cookie} title="Meet to Keep Cookie Policy" lastUpdated="July 18, 2026" />
      <LegalLanguageBar docKey="cookies" />

      <P>This Cookie Policy explains how Meet to Keep (&ldquo;Meet to Keep,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) uses cookies and similar technologies when you visit our website, use our mobile applications, or interact with our online services (collectively, the &ldquo;Services&rdquo;).</P>
      <P>By continuing to use our Services, you acknowledge that cookies and similar technologies may be used as described in this Cookie Policy, subject to your preferences and applicable law.</P>

      <Section number={1} title="What Are Cookies?">
        <P>Cookies are small text files stored on your device by your web browser when you visit a website.</P>
        <P>Cookies help websites:</P>
        <Bullet>Remember your preferences</Bullet>
        <Bullet>Keep you signed in</Bullet>
        <Bullet>Improve website performance</Bullet>
        <Bullet>Enhance security</Bullet>
        <Bullet>Understand how visitors use the Services</Bullet>
        <P>Some cookies are deleted when you close your browser, while others remain on your device until they expire or are deleted.</P>
      </Section>

      <Section number={2} title="Similar Technologies">
        <P>In addition to cookies, Meet to Keep may use technologies such as:</P>
        <Bullet>Local storage</Bullet>
        <Bullet>Session storage</Bullet>
        <Bullet>Software Development Kits (SDKs)</Bullet>
        <Bullet>Pixels</Bullet>
        <Bullet>Web beacons</Bullet>
        <Bullet>Device identifiers</Bullet>
        <Bullet>Similar tracking technologies</Bullet>
        <P>These technologies help us provide, secure, and improve our Services.</P>
      </Section>

      <Section number={3} title="Types of Cookies We Use">
        <SubHeading>Essential Cookies</SubHeading>
        <P>These cookies are necessary for the operation of Meet to Keep.</P>
        <P>Examples include:</P>
        <Bullet>User authentication</Bullet>
        <Bullet>Login sessions</Bullet>
        <Bullet>Security protection</Bullet>
        <Bullet>Fraud prevention</Bullet>
        <Bullet>Load balancing</Bullet>
        <Bullet>Remembering privacy choices</Bullet>
        <P>Without these cookies, certain features may not function properly.</P>

        <SubHeading>Functional Cookies</SubHeading>
        <P>These cookies remember your preferences.</P>
        <P>Examples include:</P>
        <Bullet>Language settings</Bullet>
        <Bullet>Theme preferences</Bullet>
        <Bullet>Notification preferences</Bullet>
        <Bullet>Accessibility settings</Bullet>
        <Bullet>Saved login preferences</Bullet>

        <SubHeading>Analytics Cookies</SubHeading>
        <P>These cookies help us understand how visitors use our Services.</P>
        <P>We may collect information such as:</P>
        <Bullet>Pages visited</Bullet>
        <Bullet>Features used</Bullet>
        <Bullet>Time spent on pages</Bullet>
        <Bullet>Navigation paths</Bullet>
        <Bullet>Crash reports</Bullet>
        <Bullet>Performance metrics</Bullet>
        <P>This information helps us improve Meet to Keep.</P>

        <SubHeading>Performance Cookies</SubHeading>
        <P>These cookies help us monitor:</P>
        <Bullet>App performance</Bullet>
        <Bullet>Website speed</Bullet>
        <Bullet>Server reliability</Bullet>
        <Bullet>Error reporting</Bullet>
        <Bullet>Feature performance</Bullet>

        <SubHeading>Security Cookies</SubHeading>
        <P>Security cookies help us:</P>
        <Bullet>Detect suspicious activity</Bullet>
        <Bullet>Prevent fraud</Bullet>
        <Bullet>Protect user accounts</Bullet>
        <Bullet>Prevent unauthorized access</Bullet>
        <Bullet>Identify bots and automated abuse</Bullet>

        <SubHeading>Advertising Cookies</SubHeading>
        <P>If we display advertising or run marketing campaigns, advertising cookies may help us:</P>
        <Bullet>Measure campaign effectiveness</Bullet>
        <Bullet>Prevent duplicate advertisements</Bullet>
        <Bullet>Personalize marketing where permitted by law</Bullet>
        <P>Meet to Keep does not sell your personal information.</P>
      </Section>

      <Section number={4} title="Third-Party Technologies">
        <P>We may use trusted third-party providers to operate and improve our Services.</P>
        <P>Depending on the features you use, these providers may set or access cookies or similar technologies.</P>
        <P>Examples may include providers for:</P>
        <Bullet>Cloud hosting</Bullet>
        <Bullet>Authentication</Bullet>
        <Bullet>Analytics</Bullet>
        <Bullet>Payment processing</Bullet>
        <Bullet>Push notifications</Bullet>
        <Bullet>Customer support</Bullet>
        <Bullet>Artificial intelligence</Bullet>
        <Bullet>Email delivery</Bullet>
        <Bullet>Fraud prevention</Bullet>
        <P>These providers process information in accordance with their own privacy policies and our agreements with them.</P>
      </Section>

      <Section number={5} title="Mobile Applications">
        <P>Mobile apps do not always use browser cookies.</P>
        <P>Instead, Meet to Keep may use:</P>
        <Bullet>Device identifiers</Bullet>
        <Bullet>SDKs</Bullet>
        <Bullet>Local storage</Bullet>
        <Bullet>Secure tokens</Bullet>
        <Bullet>Similar technologies</Bullet>
        <P>These technologies serve many of the same purposes as cookies.</P>
      </Section>

      <Section number={6} title="How We Use Cookies">
        <P>We use cookies and similar technologies to:</P>
        <Bullet>Keep you signed in</Bullet>
        <Bullet>Remember your preferences</Bullet>
        <Bullet>Protect your account</Bullet>
        <Bullet>Detect fraud</Bullet>
        <Bullet>Improve performance</Bullet>
        <Bullet>Analyze usage</Bullet>
        <Bullet>Personalize your experience</Bullet>
        <Bullet>Measure the effectiveness of features</Bullet>
        <Bullet>Diagnose technical problems</Bullet>
      </Section>

      <Section number={7} title="Managing Cookies">
        <P>Most web browsers allow you to:</P>
        <Bullet>View stored cookies</Bullet>
        <Bullet>Delete cookies</Bullet>
        <Bullet>Block cookies</Bullet>
        <Bullet>Receive notifications before cookies are stored</Bullet>
        <P>Please note that disabling certain cookies may affect the functionality of Meet to Keep.</P>
      </Section>

      <Section number={8} title="Browser Controls">
        <P>You can manage cookie settings through your browser.</P>
        <P>Most browsers provide controls to:</P>
        <Bullet>Delete cookies</Bullet>
        <Bullet>Block cookies</Bullet>
        <Bullet>Block third-party cookies</Bullet>
        <Bullet>Clear browsing data</Bullet>
        <Bullet>Manage permissions</Bullet>
        <P>Instructions vary by browser.</P>
      </Section>

      <Section number={9} title="Do Not Track">
        <P>Some browsers offer a &ldquo;Do Not Track&rdquo; (DNT) setting.</P>
        <P>Because there is no universally accepted standard for responding to DNT signals, Meet to Keep does not currently respond differently to such signals unless required by applicable law.</P>
      </Section>

      <Section number={10} title="International Users">
        <P>If you access Meet to Keep from outside the United States, cookies and similar technologies may be processed in countries where we or our service providers operate.</P>
      </Section>

      <Section number={11} title="Changes to This Cookie Policy">
        <P>We may update this Cookie Policy from time to time.</P>
        <P>When we make material changes, we will update the &ldquo;Last Updated&rdquo; date and, where appropriate, provide additional notice.</P>
        <P>Your continued use of the Services after changes become effective constitutes acceptance of the updated Cookie Policy.</P>
      </Section>

      <Section number={12} title="Contact Us">
        <P>If you have questions about this Cookie Policy, please contact us:</P>
        <ContactBox>
          <p className="text-[13.5px] font-[700]" style={{ color: '#0F0F10' }}>Meet to Keep</p>
          <EmailLink label="Email" address="privacy@meettokeep.com" />
          <EmailLink label="Support" address="support@meettokeep.com" />
          <ExternalLink label="Website" href="https://meettokeep.com" />
        </ContactBox>
      </Section>

      <LegalFooter />
    </LegalPageShell>
  );
}
